import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  RefreshCw,
  Eye,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  TrendingUp,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Check,
  X,
  Copy,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
const Transactions = () => {
  const { isDarkMode } = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [paymentIdInput, setPaymentIdInput] = useState("");

  const bgClass = isDarkMode ? "bg-black" : "bg-gray-50";
  const cardBg = isDarkMode ? "bg-black" : "bg-white";
  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-600";
  const borderColor = isDarkMode ? "border-gray-800" : "border-gray-200";
  const hoverBg = isDarkMode ? "hover:bg-neutral-900" : "hover:bg-gray-50";
  const inputBg = isDarkMode ? "bg-neutral-900" : "bg-gray-100";
  const inputText = isDarkMode ? "text-white" : "text-gray-900";

  const API_BASE_URL =
    process.env.REACT_APP_BASE_URL || "http://localhost:3000";

  const getAuthToken = () => {
    return localStorage.getItem("accessToken");
  };
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/admin/payments?page=${currentPage}&limit=20&status=${
          statusFilter !== "all" ? statusFilter : ""
        }&search=${searchTerm}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // ✅ 401 CHECK
      if (data.statusCode === 401 || data.message === "Unauthorized") {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      setTransactions(data.transactions || []);
      setPagination(
        data.pagination || {
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPrevPage: false,
        }
      );
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      console.error("Error fetching transactions:", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, searchTerm, API_BASE_URL]);

  const fetchStats = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/admin/payments/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.statusCode === 401 || data.message === "Unauthorized") {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      setStats(data);
    } catch (error) {
      if (error.message.includes("401")) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
        return;
      }

      console.error("Error fetching stats:", error);
      setStats(null);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchTransactions();
    fetchStats();
  }, [fetchTransactions, fetchStats]);

  const handleSearch = () => {
    if (currentPage === 1) {
      fetchTransactions();
    } else {
      setCurrentPage(1);
    }
  };

  const handleViewDetails = async (transactionId) => {
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/admin/payments/${transactionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSelectedTransaction(data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error fetching transaction details:", error);
    }
  };

  const handleApprovePayment = async () => {
    if (!selectedTransaction) return;

    if (!paymentIdInput || paymentIdInput.trim() === "") {
      return;
    }

    setActionLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/admin/payments/${selectedTransaction._id}/approve`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ paymentId: paymentIdInput.trim() }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to approve payment");
      }

      setShowDetailsModal(false);
      setShowApproveModal(false);
      setPaymentIdInput("");
      fetchTransactions();
      fetchStats();
    } catch (error) {
      console.error("Error approving payment:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectPayment = async () => {
    if (!selectedTransaction) return;

    setActionLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/admin/payments/${selectedTransaction._id}/reject`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason: rejectReason }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to reject payment");
      }

      setShowDetailsModal(false);
      setShowRejectModal(false);
      setRejectReason("");
      fetchTransactions();
      fetchStats();
    } catch (error) {
      console.error("Error rejecting payment:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: {
        bg: "bg-green-500/20",
        text: "text-green-500",
        icon: CheckCircle,
        label: "Success",
      },
      failed: {
        bg: "bg-red-500/20",
        text: "text-red-500",
        icon: XCircle,
        label: "Failed",
      },
      pending: {
        bg: "bg-orange-500/20",
        text: "text-orange-500",
        icon: Clock,
        label: "Pending",
      },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
    document.body.removeChild(textArea);
  };

  return (
    <div className={`min-h-screen ${bgClass} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${textPrimary}`}>
              Transactions
            </h1>
            <p className={`${textSecondary} mt-1`}>
              Monitor and manage payment transactions
            </p>
          </div>
          <button
            onClick={fetchTransactions}
            className={`flex items-center gap-2 px-4 py-2 ${cardBg} ${textPrimary} rounded-lg border ${borderColor} ${hoverBg} transition-colors`}
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`${cardBg} rounded-lg p-6 border ${borderColor}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textSecondary}`}>Total Revenue</p>
                  <p className={`text-2xl font-bold ${textPrimary} mt-1`}>
                    {formatCurrency(stats.totalRevenue)}
                  </p>
                </div>
                <div className="bg-green-500/20 p-3 rounded-lg">
                  <TrendingUp className="text-green-500" size={24} />
                </div>
              </div>
              <p className="text-xs text-green-500 mt-2">
                Success Rate: {stats.successRate}%
              </p>
            </div>

            <div className={`${cardBg} rounded-lg p-6 border ${borderColor}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textSecondary}`}>
                    Total Transactions
                  </p>
                  <p className={`text-2xl font-bold ${textPrimary} mt-1`}>
                    {stats.totalTransactions}
                  </p>
                </div>
                <div className="bg-blue-500/20 p-3 rounded-lg">
                  <CreditCard className="text-blue-500" size={24} />
                </div>
              </div>
              <p className={`text-xs ${textSecondary} mt-2`}>
                Avg: {formatCurrency(stats.avgTransactionAmount)}
              </p>
            </div>

            <div className={`${cardBg} rounded-lg p-6 border ${borderColor}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textSecondary}`}>Successful</p>
                  <p className={`text-2xl font-bold text-green-500 mt-1`}>
                    {stats.successCount}
                  </p>
                </div>
                <div className="bg-green-500/20 p-3 rounded-lg">
                  <CheckCircle className="text-green-500" size={24} />
                </div>
              </div>
            </div>

            <div className={`${cardBg} rounded-lg p-6 border ${borderColor}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${textSecondary}`}>Failed/Pending</p>
                  <p className={`text-2xl font-bold text-red-500 mt-1`}>
                    {stats.failedCount + stats.pendingCount}
                  </p>
                </div>
                <div className="bg-red-500/20 p-3 rounded-lg">
                  <AlertCircle className="text-red-500" size={24} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className={`${cardBg} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 flex items-center gap-2">
              <div className="relative flex-1">
                <Search
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${textSecondary}`}
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Search by Order ID or Payment ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className={`w-full pl-10 pr-4 py-2 ${inputBg} ${inputText} rounded-lg border ${borderColor} focus:outline-none focus:ring-2 focus:ring-orange-500`}
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-600 transition-colors"
              >
                Search
              </button>
            </div>

            <div className="flex items-center gap-2">
              {["all", "success", "pending", "failed"].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setStatusFilter(status);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    statusFilter === status
                      ? "bg-orange-500 text-black"
                      : `${inputBg} ${textPrimary} ${hoverBg}`
                  }`}
                >
                  {status === "all"
                    ? "All"
                    : status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div
          className={`${cardBg} rounded-lg border ${borderColor} overflow-hidden`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={isDarkMode ? "bg-neutral-900" : "bg-gray-50"}>
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${textSecondary} uppercase tracking-wider`}
                  >
                    Order ID
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${textSecondary} uppercase tracking-wider`}
                  >
                    User
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${textSecondary} uppercase tracking-wider`}
                  >
                    Amount
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${textSecondary} uppercase tracking-wider`}
                  >
                    Status
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${textSecondary} uppercase tracking-wider`}
                  >
                    Date
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${textSecondary} uppercase tracking-wider`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${borderColor}`}>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <RefreshCw
                          className={`animate-spin ${textSecondary}`}
                          size={24}
                        />
                      </div>
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className={`px-6 py-12 text-center ${textSecondary}`}
                    >
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <tr key={transaction._id} className={hoverBg}>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm font-mono ${textPrimary}`}
                      >
                        {transaction.orderId}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${textPrimary}`}
                      >
                        <div>
                          <div className="font-medium">
                            {transaction.userId?.firstName}{" "}
                            {transaction.userId?.lastName}
                          </div>
                          <div className={`text-xs ${textSecondary}`}>
                            {transaction.userId?.email}
                          </div>
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${textPrimary}`}
                      >
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(transaction.status)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${textSecondary}`}
                      >
                        {formatDate(transaction.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleViewDetails(transaction._id)}
                          className={`flex items-center gap-1 px-3 py-1.5 ${inputBg} ${textPrimary} rounded-lg ${hoverBg} transition-colors`}
                        >
                          <Eye size={14} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && transactions.length > 0 && (
            <div
              className={`px-6 py-4 border-t ${borderColor} flex items-center justify-between`}
            >
              <div className={`text-sm ${textSecondary}`}>
                Showing page {currentPage} of {pagination.totalPages} (
                {pagination.total} total)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrevPage}
                  className={`p-2 ${inputBg} rounded-lg ${hoverBg} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className={`px-4 py-2 ${textPrimary}`}>
                  {currentPage}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`p-2 ${inputBg} rounded-lg ${hoverBg} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`${cardBg} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
          >
            <div className={`p-6 border-b ${borderColor}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-bold ${textPrimary}`}>
                  Transaction Details
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={`${textSecondary} hover:${textPrimary}`}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={`text-sm ${textSecondary}`}>Order ID</p>
                  <div className="flex items-center gap-2">
                    <p className={`font-mono ${textPrimary}`}>
                      {selectedTransaction.orderId}
                    </p>
                    <button
                      onClick={() => handleCopy(selectedTransaction.orderId)}
                      className={`p-1 rounded-md ${hoverBg} transition ${textSecondary}`}
                      title="Copy Order ID"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>

                <div>
                  <p className={`text-sm ${textSecondary}`}>Payment ID</p>
                  <p className={`font-mono ${textPrimary}`}>
                    {selectedTransaction.paymentId || "N/A"}
                  </p>
                </div>

                <div>
                  <p className={`text-sm ${textSecondary}`}>Amount</p>
                  <p className={`font-semibold ${textPrimary}`}>
                    {formatCurrency(selectedTransaction.amount)}
                  </p>
                </div>
                <div>
                  <p className={`text-sm ${textSecondary}`}>Status</p>
                  {getStatusBadge(selectedTransaction.status)}
                </div>
                <div>
                  <p className={`text-sm ${textSecondary}`}>Date</p>
                  <p className={textPrimary}>
                    {formatDate(selectedTransaction.createdAt)}
                  </p>
                </div>
              </div>

              <div>
                <p className={`text-sm ${textSecondary}`}>User Details</p>
                <div className={`mt-1 ${textPrimary}`}>
                  {selectedTransaction.userId?.firstName}{" "}
                  {selectedTransaction.userId?.lastName}
                  <br />
                  <span className={textSecondary}>
                    {selectedTransaction.userId?.email}
                  </span>
                </div>
              </div>

              {selectedTransaction.status === "pending" && (
                <div className={`pt-4 border-t ${borderColor}`}>
                  <p className={`text-sm ${textSecondary} mb-3`}>
                    Admin Actions
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowApproveModal(true)}
                      disabled={actionLoading}
                      className="flex-1 relative overflow-hidden group flex items-center justify-center gap-2 
                px-5 py-2.5 font-semibold rounded-xl text-green-400 border border-green-400 
                transition-all duration-300 hover:text-white 
                disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span
                        className="absolute inset-0 bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"
                      ></span>
                      <span
                        className="absolute inset-0 bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"
                      ></span>

                      <span className="relative flex items-center gap-2 z-10">
                        <Check size={18} />
                        Approve & Credit Tokens
                      </span>
                    </button>

                    <button
                      onClick={() => setShowRejectModal(true)}
                      disabled={actionLoading}
                      className="flex-1 relative overflow-hidden group flex items-center justify-center gap-2 
                px-5 py-2.5 font-semibold rounded-xl text-red-400 border border-red-400 
                transition-all duration-300 hover:text-white 
                disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span
                        className="absolute inset-0 bg-gradient-to-r from-red-500 via-rose-500 to-red-600 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"
                      ></span>
                      <span
                        className="absolute inset-0 bg-gradient-to-r from-red-500 via-rose-500 to-red-600 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"
                      ></span>

                      <span className="relative flex items-center gap-2 z-10">
                        <X size={18} />
                        Reject
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className={`p-6 border-t ${borderColor} text-right`}>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Payment Modal */}
      {showApproveModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${cardBg} rounded-lg max-w-md w-full`}>
            <div className={`p-6 border-b ${borderColor}`}>
              <h2 className={`text-xl font-bold ${textPrimary}`}>
                Approve Payment
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className={`p-4 rounded-lg ${inputBg} space-y-2`}>
                <div className="flex justify-between">
                  <span className={textSecondary}>Order ID:</span>
                  <span className={`font-mono ${textPrimary}`}>
                    {selectedTransaction.orderId}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={textSecondary}>Amount:</span>
                  <span className={`font-semibold ${textPrimary}`}>
                    {formatCurrency(selectedTransaction.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={textSecondary}>Tokens to Credit:</span>
                  <span className={`font-bold text-green-500`}>
                    {selectedTransaction.amount} tokens
                  </span>
                </div>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${textPrimary} mb-2`}
                >
                  Payment ID from Gateway *
                </label>
                <input
                  type="text"
                  value={paymentIdInput}
                  onChange={(e) => setPaymentIdInput(e.target.value)}
                  placeholder="Enter payment ID (e.g., pay_xxxxxxxxxxxxx)"
                  className={`w-full px-4 py-2 ${inputBg} ${inputText} rounded-lg border ${borderColor} focus:outline-none focus:ring-2 focus:ring-green-500`}
                />
                <p className={`text-xs ${textSecondary} mt-1`}>
                  Enter the payment ID received from the payment gateway
                  (Razorpay, Stripe, etc.)
                </p>
              </div>

              <div
                className={`p-3 rounded-lg bg-orange-500/10 border border-orange-500/30`}
              >
                <p className="text-sm text-orange-500">
                  This action will credit {selectedTransaction.amount} tokens to
                  the user's wallet and mark the payment as successful.
                </p>
              </div>
            </div>
            <div
              className={`p-6 border-t ${borderColor} flex gap-3 justify-end`}
            >
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setPaymentIdInput("");
                }}
                className={`px-4 py-2 ${inputBg} ${textPrimary} rounded-lg ${hoverBg} transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleApprovePayment}
                disabled={actionLoading || !paymentIdInput.trim()}
                className="px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? "Processing..." : "Confirm Approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${cardBg} rounded-lg max-w-md w-full`}>
            <div className={`p-6 border-b ${borderColor}`}>
              <h2 className={`text-xl font-bold ${textPrimary}`}>
                Reject Payment
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <p className={textSecondary}>
                Please provide a reason for rejecting this payment:
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason (optional)"
                style={{ height: "150px", resize: "none" }}
                className={`w-full px-4 py-2 ${inputBg} ${inputText} rounded-lg border ${borderColor} focus:outline-none focus:ring-2 focus:ring-red-500`}
              />
            </div>
            <div
              className={`p-6 border-t ${borderColor} flex gap-3 justify-end`}
            >
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                }}
                className={`px-4 py-2 ${inputBg} ${textPrimary} rounded-lg ${hoverBg} transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectPayment}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? "Processing..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
